import { useCallback, useMemo } from 'react';
import { useAnchorCardContext } from '../context/PlpContext';
import { useRouterContext } from '../context/PlpContext';
import { useExperiencesContext } from '../components/experiences/Experiences';
import constants from '../utils/constants';
import { getAnInViewProduct } from '../utils/getAnInViewProduct';
import {
  getQueryParams,
  getQueryString,
  plpViewTypeFilter,
} from '../components/stateful-link/StatefulLink';

type PlpViewTypeData = {
  isListView: boolean;
  hasPlpViewTypeSelection: boolean;
  onViewSelectionChange: (viewName: string) => void;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const usePlpViewType = (debuggingSource: string): PlpViewTypeData => {
  const { setAnchorCard, anchorCard } = useAnchorCardContext();
  const { isListViewEnabled, isListViewFirstOn } = useExperiencesContext();
  const router = useRouterContext();

  // console.log(`usePlpViewType(${debuggingSource})`);

  const {
    PLP_VIEW_TYPES: { GRID, LIST },
  } = constants;

  const {
    query: { plpView, ...queryRest },
    pathname: href,
  } = router;

  const onViewSelectionChange = useCallback(
    (viewName: string) => {
      const firstCardPid = getAnInViewProduct();

      const newPlpViewType = viewName === LIST ? LIST : GRID;

      const newUrl = {
        pathname: href,
        query: getQueryParams(
          { ...queryRest, plpView: newPlpViewType },
          plpViewTypeFilter()
        ),
      };

      const newAs = `${window.location.pathname}?${getQueryString(newUrl.query)}`;

      router.push(newUrl, newAs, { shallow: true, scroll: false });

      if (window.scrollY) {
        setAnchorCard(firstCardPid);
      } else {
        if (anchorCard) setAnchorCard(null);
      }
    },
    [queryRest, router, setAnchorCard, GRID, LIST]
  );

  const value = useMemo(() => {
    const isListView =
      isListViewEnabled &&
      ((!plpView && isListViewFirstOn) || plpView === LIST);

    return {
      isListView,
      hasPlpViewTypeSelection: isListViewEnabled,
      onViewSelectionChange,
    };
  }, [
    isListViewEnabled,
    isListViewFirstOn,
    onViewSelectionChange,
    plpView,
    LIST,
  ]);

  return value;
};
